// Detalhe de UMA ligação (aberto ao clicar numa linha do Relatório de Ligações).
// Mostra as infos da chamada e — quando há gravação — um player de áudio custom
// (play/pause, barra de progresso arrastável, tempo e download). A URL da gravação
// é assinada e temporária: só é buscada quando o diálogo abre (getCallRecordingUrl).
import { useTranslation } from "react-i18next";
import { ArrowDownLeft, ArrowUpRight, Clock3, ExternalLink } from "lucide-react";
import { type CallListItem } from "./api.js";
import { useDispositions } from "@/hooks/use-dispositions";
import { useLead } from "@/hooks/use-leads";
import { useCallRecording, useCallTranscription } from "@/hooks/use-calls";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, formatDuration, formatDateTime, formatPhone } from "./calls-common.js";
import { AudioPlayer } from "./softphone/audio-player.js";
import { buildCallTimeline, normalizeTranscript } from "./call-review.js";
import { CallRecordingActions } from "./call-recording-actions.js";
import "./i18n.js";

// O lead do deep-link do HubSpot não muda no minuto em que o diálogo fica
// aberto — reabrir não precisa de outro request.
const LEAD_STALE_MS = 60_000;

// Uma linha do grid de infos. Renderiza só quando há valor (children).
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function CallDetailDialog({
  call,
  open,
  onOpenChange,
  canDeleteRecording = false,
}: {
  call: CallListItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  canDeleteRecording?: boolean;
}) {
  const { t } = useTranslation();
  // URL assinada só quando o diálogo abre e a ligação tem gravação.
  const recording = useCallRecording(call?.id, { enabled: open && !!call?.recordingName });

  // Transcrição só quando o diálogo abre e há gravação (dela deriva o texto).
  // Buscada à parte da listagem para não carregar texto longo em cada linha.
  const transcription = useCallTranscription(call?.id, {
    enabled: open && !!call?.recordingName,
  });

  // O lead do qual esta ligação saiu — buscado só para o deep-link do HubSpot,
  // que o back monta (portal + external id) e a ligação não carrega: a listagem
  // traz leadId/leadName e mais nada. Mesma chave da ficha do lead, então quem
  // já abriu a ficha reaproveita o cache. Sem leadId (ligação avulsa, acesso
  // temporário) a consulta nem sai.
  const lead = useLead(call?.leadId, {
    enabled: open && !!call?.leadId,
    staleTime: LEAD_STALE_MS,
  });

  // Rótulo da tabulação: mapeia dispositionId → label pela lista da org (cacheada).
  const { data: dispositions } = useDispositions({ enabled: open });
  const dispositionLabel = call?.dispositionId
    ? (dispositions?.find((d) => d.id === call.dispositionId)?.label ?? null)
    : call?.failureReason
      ? null
      : call?.status === "COMPLETED"
        ? t("calls.common.completedDisposition")
        : null;

  const inbound = call?.direction === "INBOUND";
  const number = inbound ? call?.callerDid : call?.targetPhone;
  const outHidden = call?.direction === "OUTBOUND" && call?.targetPhoneHidden;
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight;
  const timeline = call ? buildCallTimeline(call) : [];
  const transcriptLines = normalizeTranscript(
    transcription.data?.transcription ?? null,
    call?.duration,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {call && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Icon
                  className={
                    inbound
                      ? "size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                      : "size-4 shrink-0 text-sky-600 dark:text-sky-400"
                  }
                  aria-label={t(`calls.common.direction.${call.direction}`)}
                />
                <span className="tabular-nums">
                  {outHidden ? t("calls.common.hiddenNumber") : formatPhone(number ?? null)}
                </span>
                <StatusBadge status={call.status} />
              </DialogTitle>
            </DialogHeader>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InfoRow label={t("callDetail.lead")}>
                {call.leadName ?? <span className="text-muted-foreground">—</span>}
              </InfoRow>
              <InfoRow label={t("callDetail.owner")}>
                {call.agentName ?? <span className="text-muted-foreground">—</span>}
              </InfoRow>
              <InfoRow label={t("callDetail.talkTime")}>
                <span className="tabular-nums">{formatDuration(call.duration)}</span>
              </InfoRow>
              {/* Espera = do pedido de discagem até atenderem; "—" quando
                  ninguém atendeu (não houve espera *até* o atendimento). */}
              <InfoRow label={t("callDetail.waitTime")}>
                <span className="tabular-nums">{formatDuration(call.waitSeconds)}</span>
              </InfoRow>
              <InfoRow label={t("callDetail.startedAt")}>
                <span className="tabular-nums">
                  {formatDateTime(call.startedAt ?? call.createdAt)}
                </span>
              </InfoRow>
              <InfoRow label={t("callDetail.disposition")}>
                {dispositionLabel ?? <span className="text-muted-foreground">—</span>}
              </InfoRow>
              {call.failureReason && (
                <InfoRow label={t("callDetail.failureReason")}>
                  <span className="text-destructive">{call.failureReason}</span>
                </InfoRow>
              )}
              {call.notes && (
                <div className="col-span-2">
                  <InfoRow label={t("callDetail.notes")}>{call.notes}</InfoRow>
                </div>
              )}
            </dl>

            <section aria-label={t("callDetail.review")} className="mt-1 space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  {t("callDetail.recording")}
                </p>
                <AudioPlayer
                  url={recording.data?.url ?? null}
                  downloadUrl={recording.data?.downloadUrl}
                  loading={recording.isLoading}
                />
                {call.recordingName && recording.data?.url ? (
                  <div className="mt-2">
                    <CallRecordingActions
                      callId={call.id}
                      canDelete={canDeleteRecording}
                      showDownload={false}
                      compact={false}
                      onDeleted={() => onOpenChange(false)}
                    />
                  </div>
                ) : null}
              </div>

              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Clock3 aria-hidden className="size-3.5" />
                  {t("callDetail.timeline")}
                </div>
                <ol className="grid gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 sm:grid-cols-3">
                  {timeline.map((event) => (
                    <li key={event.id} className="text-xs">
                      <p className="font-medium">{t(`callDetail.events.${event.id}`)}</p>
                      <p className="text-muted-foreground">{formatDateTime(event.at)}</p>
                    </li>
                  ))}
                </ol>
              </div>

              {call.recordingName && (
                <div>
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    {t("callDetail.transcription")}
                  </p>
                  {transcription.isLoading ? (
                    <div
                      role="status"
                      aria-busy="true"
                      aria-label={t("callDetail.loadingTranscription")}
                      className="space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3"
                    >
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-4/5" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  ) : transcriptLines.length > 0 ? (
                    <div className="max-h-64 space-y-3 overflow-y-auto rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm leading-relaxed">
                      {transcriptLines.map((line) => (
                        <p key={line.id}>
                          <span className="mr-2 text-xs font-semibold text-muted-foreground">
                            {t(`callDetail.speakers.${line.speaker}`)}
                          </span>
                          {line.text}
                        </p>
                      ))}
                      {transcriptLines.every((line) => line.startSeconds === null) && (
                        <p className="border-t border-border pt-2 text-xs text-muted-foreground">
                          {t("callDetail.noTimestamps")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                      {t("callDetail.noTranscription")}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Ações no rodapé: depois de ler a ligação inteira (tabulação,
                gravação, transcrição) a mão já está embaixo — no topo o atalho
                ficava antes do motivo de usá-lo. "Fechar" explícito porque o X
                do canto é pequeno e some no meio do cabeçalho.
                O link do HubSpot só existe quando o back devolve a URL: sem
                portal configurado ou sem external id ela vem null, e botão
                morto é pior que botão nenhum. */}
            <DialogFooter className="mt-2 sm:justify-between">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  {t("callDetail.close")}
                </Button>
              </DialogClose>
              {lead.data?.hubspotUrl && (
                <Button asChild variant="outline">
                  <a href={lead.data.hubspotUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-4" />
                    {t("callDetail.openLeadHubSpot")}
                  </a>
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
